import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { AuthContext } from '../../contexts/AuthContext';
import { Loading } from '../../components/utility/Utility';
import GoogleSignInButton from '../../components/GoogleSignInButton';
import axios from 'axios';
import { hasOAuthCallbackParams } from '../../utils/authErrors';

const API_BASE = 'https://api.amplifyloyalty.com';

export default function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { authed, authReady, userProfile, handleSignUp, handleSignInWithGoogle, authError, clearAuthError } =
    useContext(AuthContext);
  const [formErrors, setFormErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inviteMeta, setInviteMeta] = useState(null);
  const [inviteMetaStatus, setInviteMetaStatus] = useState('idle');

  const inviteTokenFromUrl = searchParams.get('invite_token');
  const hasInviteToken = Boolean(
    inviteTokenFromUrl || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('invite_token'))
  );
  const isInviteSignup = inviteMetaStatus === 'valid';
  const hideCompanyName =
    isInviteSignup || (inviteMetaStatus === 'loading' && hasInviteToken);

  const isOAuthReturn = hasOAuthCallbackParams(location.search);
  const isWaitingForAuth = !authReady || (authed && !userProfile) || isOAuthReturn;

  useEffect(() => {
    const token = inviteTokenFromUrl || sessionStorage.getItem('invite_token');
    if (!token) {
      setInviteMeta(null);
      setInviteMetaStatus('idle');
      return;
    }
    sessionStorage.setItem('invite_token', token);

    let cancelled = false;
    setInviteMetaStatus('loading');
    axios
      .get(`${API_BASE}/app/team/invite/meta`, { params: { token } })
      .then((res) => {
        if (cancelled) return;
        setInviteMeta(res.data);
        setInviteMetaStatus('valid');
      })
      .catch(() => {
        if (cancelled) return;
        setInviteMeta(null);
        setInviteMetaStatus('invalid');
      });

    return () => {
      cancelled = true;
    };
  }, [inviteTokenFromUrl]);

  useEffect(() => {
    // On component mount, check for the Shopify auth success flag.
    // If it's present, set the post-auth redirect path in session storage.
    const params = new URLSearchParams(window.location.search);
    if (params.get('shopify_auth') === 'success') {
      sessionStorage.setItem('postAuthRedirect', '/app/integrations/shopify?polling=true');
    }
  }, []);

  useEffect(() => {
    if (!authReady || !authed || !userProfile) return;

    const inviteToken = sessionStorage.getItem('invite_token');
    if (inviteToken) {
      sessionStorage.removeItem('invite_token');
      navigate(`/invite?token=${encodeURIComponent(inviteToken)}`, { replace: true });
      return;
    }

    const redirectPath = sessionStorage.getItem('postAuthRedirect');
    if (redirectPath) {
      sessionStorage.removeItem('postAuthRedirect');
      navigate(redirectPath);
    } else {
      navigate('/app/product-analysis/analytics');
    }
  }, [authReady, authed, userProfile, navigate]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string()
          .email('Invalid email address')
          .required('Field is required.'),
        password: Yup.string()
          .required('Field is required.')
          .min(8, 'Password must be at least 8 characters')
          .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
          .matches(/[a-z]/, 'Password must contain at least one lowercase letter.')
          .matches(/\d/, 'Password must contain at least one number.')
          .matches(
            /[!@#$%^&*(),.?":{}|<>]/,
            'Password must contain at least one special character'
          ),
        storeName:
          hideCompanyName && inviteMetaStatus !== 'invalid'
            ? Yup.string()
            : Yup.string().required('Please enter the name of your company.'),
        agreeToTerms: Yup.boolean()
          .oneOf([true], 'You must agree to the Terms & Conditions and Privacy Policy')
          .required('You must agree to the Terms & Conditions and Privacy Policy')
      }),
    [hideCompanyName, inviteMetaStatus]
  );

  useEffect(() => {
    let toDisable = [...document.querySelectorAll('.signup__form input')];
    toDisable.forEach(inp => inp.disabled = submitting);
  }, [submitting]);

  const onGoogleSignIn = async () => {
    setSubmitting(true);
    setFormErrors('');
    clearAuthError();
    try {
      await handleSignInWithGoogle();
    } catch (err) {
      setFormErrors(err.message || 'Google sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isWaitingForAuth && !authError && !formErrors) {
    return (
      <div className="signup">
        <h1 className='signup__header'>Sign Up</h1>
        <hr className="signup__horizontal-rule" />
        <div className="signup__oauth-wait">
          <Loading className="signup__loading" />
          <p style={{ marginTop: '0.75rem', color: '#666' }}>Completing sign in...</p>
        </div>
      </div>
    );
  }

  const pageTitle =
    isInviteSignup && inviteMeta?.storeName
      ? `Join ${inviteMeta.storeName}`
      : 'Sign Up';

  return (
    <div className="signup">
      <h1 className='signup__header'>{pageTitle}</h1>
      <hr className="signup__horizontal-rule" />
      {isInviteSignup && (
        <p className="signup__form-description" style={{ marginBottom: '1rem' }}>
          Create an account with the email that received the invite. You&apos;ll join the team after
          you confirm your email and sign in.
        </p>
      )}
      <Formik
        initialValues={{ email: '', password: '', storeName: '', agreeToTerms: false }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const userAttributes = { 'custom:timezone': timeZone };
          if (!hideCompanyName && values.storeName?.trim()) {
            userAttributes['custom:store_name'] = values.storeName.trim();
          }
          setSubmitting(true);
          try {
            await handleSignUp(values.email, values.password, userAttributes);
            
            // Fire Facebook Pixel event for successful registration
            if (window.fbq) {
              window.fbq('track', 'CompleteRegistration');
            }

            // Fire Klaviyo event for successful registration
            axios.post('https://api.amplifyloyalty.com/events/signup', { email: values.email });

            navigate('/confirm-signup', { state: { email: values.email } });
          } catch (err) {
            setFormErrors(err.message || 'An unexpected error occurred.');
          } finally {
            setSubmitting(false);
          }
        }}
        enableReinitialize={true}
      >
        {({ values }) => (
        <Form id="sign-up-form" className="auth-form signup__form">
          <label className="signup__form-label" htmlFor="email">Email</label>
          <Field name="email" type="email" placeholder="customer@gmail.com" />
          <ErrorMessage component="div" className="form-error" name="email" />

          <label className="signup__form-label" htmlFor="password">Password</label>
          <Field name="password" type="password" />
          <ErrorMessage component="div" className="form-error" name="password" />

          {!hideCompanyName && (
            <>
              <label className="signup__form-label" htmlFor="storeName">Company name</label>
              <Field name="storeName" />
              <ErrorMessage component="div" className="form-error" name="storeName" />
            </>
          )}

          {(authError || formErrors) && (
            <div className="form-errors" role="alert">
              {authError || formErrors}
            </div>
          )}
          
          {!submitting && (
            <div className="signup__terms-checkbox">
              <label className="signup__terms-label">
                <Field type="checkbox" name="agreeToTerms" className="signup__terms-checkbox-input" />
                <span className="signup__terms-text">
                  I agree to the{' '}
                  <a href="/terms-conditions" target="_blank" rel="noopener noreferrer">
                    Terms & Conditions
                  </a>
                  {' '}and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </span>
              </label>
              <ErrorMessage component="div" className="form-error" name="agreeToTerms" />
            </div>
          )}
          
          {submitting && <Loading className="signup__loading" />}
          {!submitting &&
            <>
              <a className="login-form__text" href="/login">Already have an account? Login.</a>
              <button 
                type="submit" 
                className="gradient" 
                disabled={
                  !values.agreeToTerms ||
                  (hasInviteToken && inviteMetaStatus === 'loading')
                }
                style={{
                  opacity:
                    values.agreeToTerms && !(hasInviteToken && inviteMetaStatus === 'loading')
                      ? 1
                      : 0.5,
                  cursor:
                    values.agreeToTerms && !(hasInviteToken && inviteMetaStatus === 'loading')
                      ? 'pointer'
                      : 'not-allowed'
                }}
              >
                Create account
              </button>
              <GoogleSignInButton
                variant="signup"
                disabled={submitting}
                onClick={onGoogleSignIn}
              />
            </>
          }
        </Form>
        )}
      </Formik>
    </div>
  );
}
